/**
 * Copyright (C) Siemens AG 1996-2026 ALL RIGHTS RESERVED.
 *
 * Helpers for single-object `document` fields.
 *
 * A ui:tree leaf of type `document` either stores a plain URL string (legacy
 * schemas, e.g. battery 1.x, machinery) or — when it declares sub-`attributes`
 * (battery 2.0.0) — an object such as `{url, language, resourceTitle}`.
 */

import {FormFieldAttribute} from '../../../models/FormCategory';

/** Fallback key of the URL sub-field when no sub-attribute is typed as a URL. */
const DEFAULT_URL_PATH = 'url';

/**
 * Returns whether the attribute is a single-object document, i.e. a `document`
 * field that declares sub-attributes. Plain `document` fields (URL string) and
 * `list:document` fields are not affected.
 */
export function isDocumentObjectAttribute(
  attribute: FormFieldAttribute | undefined,
): boolean {
  return attribute?.type === 'document' && !!attribute.attributes?.length;
}

/**
 * Returns the (relative) value path of the sub-attribute holding the URL.
 *
 * @param attribute - A single-object document attribute.
 */
export function getDocumentUrlPath(attribute: FormFieldAttribute): string {
  return (
    attribute.attributes?.find(
      (sub) => sub.type === 'url' || sub.type === 'document',
    )?.valuePath ?? DEFAULT_URL_PATH
  );
}

/**
 * Normalises a stored document value to its object form. A legacy plain
 * string is converted to `{[urlPath]: value}` so already-entered data is kept.
 *
 * @param value - The value stored at the document's value path.
 * @param urlPath - The relative path of the URL sub-field.
 * @returns The document object, or `undefined` when there is no value.
 */
export function toDocumentObject(
  value: unknown,
  urlPath: string = DEFAULT_URL_PATH,
): Record<string, unknown> | undefined {
  if (typeof value === 'string') {
    return value.trim() ? {[urlPath]: value.trim()} : undefined;
  }
  if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return undefined;
}

/**
 * Returns the URL if it is an absolute `http`/`https` URL, otherwise an empty
 * string, so stored values such as `javascript:` can never become a link.
 */
export function toSafeHttpUrl(url: unknown): string {
  if (typeof url !== 'string') {
    return '';
  }
  try {
    const {protocol} = new URL(url.trim());
    return protocol === 'http:' || protocol === 'https:' ? url.trim() : '';
  } catch {
    return '';
  }
}
